import { useEffect, useRef, useState, useCallback } from 'react';
import { isAuthFailureStatus, notifyAuthExpired } from '../utils/authExpiry';

/**
 * useHistorySnapshots - save + undo/redo for design editors.
 *
 * Owner's requirement (2026-10-26): an edited design must be saveable
 * so it does NOT snap back to the old style on reload, and stepping
 * back must be an explicit Undo (with Redo alongside).
 *
 * How it works, editor-agnostically:
 * - The editor describes its design as a JSON snapshot (snapshot()),
 *   knows how to restore one (apply()), and lists the state values
 *   that signal a design change (watch).
 * - Changes are recorded (debounced) onto an undo stack; Undo/Redo
 *   walk the stack by calling apply().
 * - Save writes the current snapshot to localStorage under
 *   storageKey; on mount, a saved snapshot is restored automatically,
 *   so the saved design survives reloads and revisits.
 * - Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z / Ctrl+Y work outside text fields.
 */

export interface HistorySnapshotOptions {
    storageKey: string;
    snapshot: () => any;
    apply: (snap: any) => void;
    watch: unknown[];
    debounceMs?: number;
    historyLimit?: number;
    /** Return a smaller snapshot for storage when the full one is too big (e.g. embedded images). */
    stripForSave?: (snap: any) => any;
    /**
     * Continuous working-state persistence (opt-in): every recorded
     * change (and every applied snapshot) is written, debounced with
     * the history recorder, to a versioned working-state key
     * (`<storageKey>:work`). On mount the working state is restored
     * FIRST - it is the exact live state the user left, so a refresh
     * (page reload, font-load repaint remount) continues the design
     * instead of restarting it. The explicit Save key keeps its own
     * semantics and remains the fallback when no working state exists.
     */
    autoPersistWork?: boolean;
    /**
     * Smaller snapshot used only when the full working state does not
     * fit localStorage (quota fallback before stripForSave). Lets an
     * editor keep the rendered essentials (e.g. the placed logo) while
     * dropping re-derivable bulk (e.g. the raw upload).
     */
    stripForWorkSave?: (snap: any) => any;
    /**
     * Start-options mode (opt-in; Princewill's ruling, 2026-11-04):
     * the studio lands on its factory state and a previous check is
     * an explicit option, never a silent restore. On mount, a stored
     * non-blank state is NOT applied; it is preserved in its own slot
     * (`<storageKey>:previous`) and exposed as pendingPrevious for the
     * editor's start-choice UI. Requires isBlankState so the editor
     * decides what counts as blank. The slot's rotation rules (no
     * silent loss) are documented at the mount effect.
     */
    deferRestore?: boolean;
    /** Editor-supplied test: does this snapshot carry no user content worth resuming? */
    isBlankState?: (snap: any) => boolean;
    /**
     * Editor kind for account-saved designs ('check', 'tax', 'globaldoc',
     * 'invoice'). Omit to keep saving browser-local only. When set and the
     * user is signed in, Save Design also writes the design to their
     * account on the server, so it follows them across devices - the
     * numbers inside the snapshot included.
     */
    designKind?: string;
    /** Human name for a new account design, read from the editor's current state. */
    designName?: () => string;
}

export interface AccountDesign {
    id: string;
    name: string;
    kind: string;
    created_at: string;
    updated_at: string;
}

export interface HistorySnapshots {
    undo: () => void;
    redo: () => void;
    save: () => 'full' | 'partial' | false;
    canUndo: boolean;
    canRedo: boolean;
    dirty: boolean;
    savedAt: number | null;
    saveNote: string | null;
    /** Account sync (present only when the editor set designKind). */
    accountEnabled: boolean;
    accountDesigns: AccountDesign[];
    accountId: string | null;
    accountBusy: boolean;
    saveToAccount: () => Promise<boolean>;
    loadAccountDesign: (id: string) => Promise<boolean>;
    /**
     * The design held by the explicit-Save copy in this browser
     * (name + save time), or null when nothing has been saved here.
     * The My Designs picker lists it next to any account designs, so
     * a guest's saved design is openable too - before this, the only
     * picker listed account designs and a guest who had just saved
     * had nothing the picker could show.
     */
    localSaved: { at: number; name: string } | null;
    /** Load the locally saved design (the explicit-Save copy) as the current design. */
    loadLocalSaved: () => boolean;
    deleteAccountDesign: (id: string) => Promise<void>;
    refreshAccountDesigns: () => Promise<void>;
    /** Apply an externally fetched snapshot as the current design. */
    restore: (snap: any) => void;
    /** Remove the persisted working state (explicit fresh start). */
    clearWork: () => void;
    /** Write the current snapshot to the working state immediately. */
    persistWorkNow: () => void;
    /** Start options (deferRestore editors): the preserved previous state awaiting the user's choice, or null. */
    pendingPrevious: any | null;
    /** Restore the pending previous state exactly (and unpin it - it is the live design now). */
    acceptPrevious: () => void;
    /** Hide the start offer for this session ("Start blank"); the pinned previous state is kept. */
    dismissPrevious: () => void;
    /** Delete the pinned previous state outright; also hides the offer. */
    clearPrevious: () => void;
    /**
     * Pin the CURRENT snapshot as the previous check, right now
     * (deferRestore editors). Used by an explicit "Start a blank
     * check" action: the check being left is pinned at clear time -
     * with the same deliberate save-over standing as an explicit
     * Save - so it survives the clear and is offered by the start
     * options on the next visit. A blank current state pins nothing
     * and never clobbers an existing pin.
     */
    pinPreviousNow: () => void;
}

/** Storage key of the versioned working-state envelope. */
export const workStateKey = (storageKey: string): string => `${storageKey}:work`;

/** Storage key of the pinned previous state (start-options mode). */
export const previousStateKey = (storageKey: string): string => `${storageKey}:previous`;

/** Serialize a working state (versioned envelope; state stored verbatim). */
export const serializeWorkState = (state: any): string =>
    JSON.stringify({ v: 1, at: Date.now(), state });

/**
 * Parse a working-state envelope. Returns the stored state VERBATIM
 * (the exact values the user left - restore never re-derives), or
 * null for anything that is not a v1 envelope, so a legacy or corrupt
 * value under the key can never corrupt the editor.
 */
export const parseWorkState = (raw: string | null): any | null => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.v === 1 && parsed.state && typeof parsed.state === 'object') {
            return parsed.state;
        }
    } catch { /* corrupt: treated as absent */ }
    return null;
};

const authToken = (): string | null => {
    try { return localStorage.getItem('token'); } catch { return null; }
};

const ser = (v: any): string => {
    try { return JSON.stringify(v) ?? ''; } catch { return ''; }
};

export function useHistorySnapshots(opts: HistorySnapshotOptions): HistorySnapshots {
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);
    const [saveNote, setSaveNote] = useState<string | null>(null);
    // Start options: the pinned previous state awaiting the user's
    // choice (deferRestore editors only; null = no offer).
    const [pendingPrevious, setPendingPrevious] = useState<any>(null);

    // ---- Account-saved designs (server sync) ----
    const [accountEnabled, setAccountEnabled] = useState<boolean>(() => Boolean(opts.designKind && authToken()));
    const [accountDesigns, setAccountDesigns] = useState<AccountDesign[]>([]);
    const [accountId, setAccountId] = useState<string | null>(null);
    const [accountBusy, setAccountBusy] = useState(false);
    // The explicit-Save copy in this browser, surfaced for the My
    // Designs picker (name + time), whatever the restore path did.
    const [localSaved, setLocalSaved] = useState<{ at: number; name: string } | null>(null);

    const acctKey = () => `${optsRef.current.storageKey}:acct`;
    const setLinkedAccountId = useCallback((id: string | null) => {
        setAccountId(id);
        try {
            if (id) localStorage.setItem(acctKey(), id);
            else localStorage.removeItem(acctKey());
        } catch { /* storage unavailable */ }
    }, []);

    const refreshAccountDesigns = useCallback(async () => {
        const token = authToken();
        if (!optsRef.current.designKind || !token) return;
        try {
            const res = await fetch('/api/designs', { headers: { 'Authorization': `Bearer ${token}` } });
            if (isAuthFailureStatus(res.status)) {
                // The stored token is dead (expired answers 401 from
                // the middleware; older deployments answered 403).
                // Stop presenting account saving as available and let
                // AuthContext reconcile the whole app to signed-out.
                setAccountEnabled(false);
                setAccountDesigns([]);
                notifyAuthExpired();
                return;
            }
            const json = await res.json().catch(() => null);
            if (json?.success && Array.isArray(json.designs)) {
                setAccountDesigns(json.designs.filter((d: AccountDesign) => d.kind === optsRef.current.designKind));
            }
        } catch { /* offline or server down: local save still works */ }
    }, []);

    // On mount: restore the linked account-design id and pull the list.
    useEffect(() => {
        if (!optsRef.current.designKind || !authToken()) return;
        try {
            const linked = localStorage.getItem(acctKey());
            if (linked) setAccountId(linked);
        } catch { /* ignore */ }
        refreshAccountDesigns();
    }, []);

    const optsRef = useRef(opts);
    optsRef.current = opts;
    const pastRef = useRef<any[]>([]);
    const futureRef = useRef<any[]>([]);
    const presentRef = useRef<any>(null);
    const applyingRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ---- Working-state persistence (autoPersistWork editors) ----
    const workKey = useCallback(() => workStateKey(optsRef.current.storageKey), []);
    const prevKey = useCallback(() => previousStateKey(optsRef.current.storageKey), []);
    const persistWork = useCallback((snap: any) => {
        if (!optsRef.current.autoPersistWork || snap == null) return;
        // Full state first; on quota, fall back to progressively
        // smaller snapshots. Silent on total failure: auto-persist
        // must never break editing (explicit Save still reports).
        const candidates: any[] = [snap];
        if (optsRef.current.stripForWorkSave) candidates.push(optsRef.current.stripForWorkSave(snap));
        if (optsRef.current.stripForSave) candidates.push(optsRef.current.stripForSave(snap));
        for (const candidate of candidates) {
            try {
                localStorage.setItem(workKey(), serializeWorkState(candidate));
                return;
            } catch { /* try the next, smaller candidate */ }
        }
    }, [workKey]);

    const clearWork = useCallback(() => {
        try { localStorage.removeItem(workKey()); } catch { /* storage unavailable */ }
    }, [workKey]);

    const persistWorkNow = useCallback(() => {
        persistWork(optsRef.current.snapshot());
    }, [persistWork]);

    // Mount: restore the design the user left and seed the history
    // with whatever the editor then holds, so Undo can always return
    // here. Restore precedence:
    //   1. the working state (autoPersistWork) - the exact live state
    //      from the last session, restored verbatim via apply();
    //   2. the explicit Save snapshot (original behaviour; also the
    //      migration source - a restore from it seeds the working
    //      state so the next reload takes the working-state path).
    useEffect(() => {
        let initial = optsRef.current.snapshot();
        let restored: any = null;
        if (optsRef.current.autoPersistWork) {
            try { restored = parseWorkState(localStorage.getItem(workKey())); }
            catch { restored = null; }
        }
        // Surface the explicit-Save copy for the My Designs picker
        // whatever the restore path: it is the design the user chose
        // to keep, and a guest's picker has nothing else to list.
        // (The restore logic below re-reads the same envelope for
        // its own fallback; the picker only needs name + time.)
        try {
            const raw = localStorage.getItem(optsRef.current.storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.state) {
                    setLocalSaved({
                        at: parsed.at || 0,
                        name: (typeof parsed.name === 'string' && parsed.name.trim()) ? parsed.name : 'Saved design'
                    });
                }
            }
        } catch { /* storage unavailable or corrupt: no local saved design */ }
        if (restored == null) {
            try {
                const raw = localStorage.getItem(optsRef.current.storageKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed && parsed.state) {
                        restored = parsed.state;
                        if (parsed.at) setSavedAt(parsed.at);
                    }
                }
            } catch { /* storage unavailable or corrupt: start from defaults */ }
        }
        if (optsRef.current.deferRestore) {
            // Start-options mode: land on the editor's factory state
            // and offer the previous check as an explicit choice.
            // The pinned previous slot (<storageKey>:previous) follows
            // a rotation that guarantees no silent loss:
            //  - a non-blank departure is pinned ONLY when the slot is
            //    empty; pinning frees the working key, so the new
            //    session's edits persist on their own track and can
            //    never overwrite the pinned check;
            //  - while a check is pinned, choosing "Start blank" (or
            //    editing the blank check) never replaces it - the
            //    offer returns on the next visit;
            //  - accepting the pinned check unpins it (it is the live
            //    design now), so the NEXT departure becomes the new
            //    previous check;
            //  - an explicit Save Design is a deliberate save-over and
            //    re-pins the slot to the saved design;
            //  - an explicit "Start a blank check" pins the check
            //    being left at clear time (pinPreviousNow, same
            //    deliberate save-over standing as a Save), so the
            //    filled check - not the blank it cleared to - is what
            //    the next visit offers;
            //  - a design-only reset (Default Clean Style) never
            //    touches the slot: it clears nothing, so nothing
            //    needs protecting from it.
            const isBlank = (s: any) => (optsRef.current.isBlankState ? optsRef.current.isBlankState(s) : false);
            let offered: any = null;
            try { offered = parseWorkState(localStorage.getItem(prevKey())); } catch { offered = null; }
            if (offered != null && isBlank(offered)) offered = null;
            if (restored != null && !isBlank(restored) && offered == null) {
                let pinned = false;
                try { localStorage.setItem(prevKey(), serializeWorkState(restored)); pinned = true; }
                catch { /* quota: the working state stays as the fallback copy */ }
                if (pinned) clearWork();
                offered = restored;
            }
            if (offered != null) setPendingPrevious(offered);
            presentRef.current = initial;
            return;
        }
        if (restored != null) {
            applyingRef.current = true;
            optsRef.current.apply(restored);
            initial = restored;
            // The watch effect's next pass treats the applied state as
            // the present rather than a new edit.
            setTimeout(() => { applyingRef.current = false; }, 1200);
            persistWork(restored);
        }
        presentRef.current = initial;
    }, []);

    // Record design changes onto the undo stack (debounced so a drag or
    // a burst of typing becomes one step).
    useEffect(() => {
        // Sign-in can happen while the editor stays mounted (auth is a
        // modal over the studio). The moment a token exists, enable
        // account saving and pull the user's design list.
        if (optsRef.current.designKind && authToken() && !accountEnabled) {
            setAccountEnabled(true);
            refreshAccountDesigns();
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            const snap = optsRef.current.snapshot();
            if (applyingRef.current) {
                // This change came from apply() (restore/undo/redo):
                // adopt it as the present without recording a step.
                presentRef.current = snap;
                applyingRef.current = false;
                persistWork(snap);
                return;
            }
            if (presentRef.current == null) { presentRef.current = snap; return; }
            if (ser(snap) === ser(presentRef.current)) return;
            pastRef.current.push(presentRef.current);
            const limit = optsRef.current.historyLimit ?? 60;
            if (pastRef.current.length > limit) pastRef.current.shift();
            presentRef.current = snap;
            persistWork(snap);
            futureRef.current = [];
            setCanUndo(true);
            setCanRedo(false);
            setDirty(true);
        }, opts.debounceMs ?? 350);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, opts.watch);

    const applySnap = useCallback((snap: any) => {
        applyingRef.current = true;
        presentRef.current = snap;
        persistWork(snap);
        optsRef.current.apply(snap);
        setDirty(true);
        // Safety net: if the applied state produces no watch change
        // (a no-op apply), don't leave the recorder muted.
        setTimeout(() => { applyingRef.current = false; }, 1200);
    }, [persistWork]);

    // ---- Start options (deferRestore editors) ----
    const acceptPrevious = useCallback(() => {
        if (pendingPrevious == null) return;
        const snap = pendingPrevious;
        setPendingPrevious(null);
        // The accepted check is the live design now - unpin it, so
        // the next departure becomes the new previous check.
        try { localStorage.removeItem(prevKey()); } catch { /* storage unavailable */ }
        applySnap(snap);
    }, [pendingPrevious, applySnap, prevKey]);

    const dismissPrevious = useCallback(() => {
        // "Start blank": hide the offer for this session only. The
        // pinned previous check stays in its slot and is offered
        // again on the next visit.
        setPendingPrevious(null);
    }, []);

    const clearPrevious = useCallback(() => {
        try { localStorage.removeItem(prevKey()); } catch { /* storage unavailable */ }
        setPendingPrevious(null);
    }, [prevKey]);

    const pinPreviousNow = useCallback(() => {
        if (!optsRef.current.deferRestore) return;
        const snap = optsRef.current.snapshot();
        if (snap == null) return;
        // Nothing worth resuming pins nothing - and a blank current
        // state must never clobber a check already pinned.
        const isBlank = optsRef.current.isBlankState ? optsRef.current.isBlankState(snap) : false;
        if (isBlank) return;
        // Same candidate ladder as persistWork: the full state
        // first, then progressively smaller snapshots on quota.
        const candidates: any[] = [snap];
        if (optsRef.current.stripForWorkSave) candidates.push(optsRef.current.stripForWorkSave(snap));
        if (optsRef.current.stripForSave) candidates.push(optsRef.current.stripForSave(snap));
        for (const candidate of candidates) {
            try { localStorage.setItem(prevKey(), serializeWorkState(candidate)); return; }
            catch { /* try the next, smaller candidate */ }
        }
    }, [prevKey]);

    const undo = useCallback(() => {
        if (!pastRef.current.length) return;
        const prev = pastRef.current.pop();
        futureRef.current.push(presentRef.current);
        applySnap(prev);
        setCanUndo(pastRef.current.length > 0);
        setCanRedo(true);
    }, [applySnap]);

    const redo = useCallback(() => {
        if (!futureRef.current.length) return;
        const next = futureRef.current.pop();
        pastRef.current.push(presentRef.current);
        applySnap(next);
        setCanRedo(futureRef.current.length > 0);
        setCanUndo(true);
    }, [applySnap]);

    const save = useCallback((): 'full' | 'partial' | false => {
        const snap = optsRef.current.snapshot();
        presentRef.current = snap;
        persistWork(snap);
        // One timestamp + the editor's current design name, shared
        // by the stored envelope and the picker's localSaved entry,
        // so what the picker shows is what Save actually wrote.
        const at = Date.now();
        const name = (optsRef.current.designName?.() || '').trim();
        const write = (value: any) => localStorage.setItem(
            optsRef.current.storageKey,
            JSON.stringify({ at, name: name || undefined, state: value })
        );
        const markSaved = () => {
            setSavedAt(at);
            setLocalSaved({ at, name: name || 'Saved design' });
            setDirty(false);
        };
        // Start-options mode: an explicit Save Design is a deliberate
        // save-over - it re-pins the previous slot to the design the
        // user just chose to save.
        const pinOnSave = (value: any) => {
            if (!optsRef.current.deferRestore) return;
            try { localStorage.setItem(prevKey(), serializeWorkState(value)); } catch { /* the Save copy itself still landed */ }
        };
        try {
            write(snap);
            pinOnSave(snap);
            markSaved();
            setSaveNote(null);
            return 'full';
        } catch {
            // Quota: retry with the editor's stripped snapshot if it has one.
            if (optsRef.current.stripForSave) {
                try {
                    const stripped = optsRef.current.stripForSave(snap);
                    write(stripped);
                    pinOnSave(stripped);
                    markSaved();
                    setSaveNote('Saved without embedded images (too large for browser storage)');
                    return 'partial';
                } catch { /* fall through */ }
            }
            setSaveNote('Save failed — browser storage is full or unavailable');
            return false;
        }
    }, [persistWork, prevKey]);

    const saveToAccount = useCallback(async (): Promise<boolean> => {
        const token = authToken();
        const kind = optsRef.current.designKind;
        if (!kind || !token) return false;
        // The user may have signed in after this editor mounted (the
        // enabled flag is first computed at mount): light it up now.
        setAccountEnabled(true);
        setAccountBusy(true);
        // A failed account save must NEVER lose the design: whatever
        // happens below, the explicit local Save copy is (re)written
        // first-class - it is what the My Designs picker lists as
        // "Saved in this browser" and what a reload restores or
        // offers back. (2026-11-04: an expired token made account
        // saves 403 while the app still looked signed in, and the
        // failure surfaced as a raw server error with the local copy
        // left to chance.)
        const secureLocalCopy = (): 'full' | 'partial' | false => save();
        try {
            const snap = optsRef.current.snapshot();
            const data = optsRef.current.stripForSave ? optsRef.current.stripForSave(snap) : snap;
            const name = (optsRef.current.designName?.() || '').trim()
                || `${kind} design — ${new Date().toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
            const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
            let res: Response;
            if (accountId) {
                res = await fetch(`/api/designs/${accountId}`, { method: 'PUT', headers, body: JSON.stringify({ name, data }) });
                if (res.status === 404) {
                    // The linked design was deleted elsewhere: save as new.
                    res = await fetch('/api/designs', { method: 'POST', headers, body: JSON.stringify({ name, kind, data }) });
                }
            } else {
                res = await fetch('/api/designs', { method: 'POST', headers, body: JSON.stringify({ name, kind, data }) });
            }
            if (isAuthFailureStatus(res.status)) {
                // Dead/expired session (401 today; 403 from older
                // deployments). Preserve the design on this device,
                // say plainly what happened, and reconcile the app
                // to signed-out - never a silent or raw-error failure.
                const local = secureLocalCopy();
                setAccountEnabled(false);
                notifyAuthExpired();
                if (local === 'full') {
                    setSaveNote("You're signed out — design saved on this device. Sign in to sync it to your account.");
                } else if (local === 'partial') {
                    setSaveNote("You're signed out — design saved on this device (without embedded images). Sign in to sync it to your account.");
                } else {
                    setSaveNote("You're signed out, and browser storage is full — the design could not be saved on this device either. Sign in to save it to your account.");
                }
                return false;
            }
            const json = await res.json().catch(() => null);
            if (!res.ok || !json?.success) {
                secureLocalCopy();
                setSaveNote(json?.error || 'Account save failed — design is saved in this browser only');
                return false;
            }
            if (json.design?.id) setLinkedAccountId(json.design.id);
            // Deliberate save-over also re-pins the previous slot
            // (start-options mode; see save()).
            if (optsRef.current.deferRestore) {
                try { localStorage.setItem(prevKey(), serializeWorkState(data)); } catch { /* the account copy still landed */ }
            }
            presentRef.current = snap;
            setSavedAt(Date.now());
            setDirty(false);
            setSaveNote(optsRef.current.stripForSave
                ? 'Saved to your account (without embedded images)'
                : 'Saved to your account');
            refreshAccountDesigns();
            return true;
        } catch {
            secureLocalCopy();
            setSaveNote('Account save failed — design is saved in this browser only');
            return false;
        } finally {
            setAccountBusy(false);
        }
    }, [accountId, refreshAccountDesigns, setLinkedAccountId, save]);

    const loadAccountDesign = useCallback(async (id: string): Promise<boolean> => {
        const token = authToken();
        if (!token) return false;
        setAccountBusy(true);
        try {
            const res = await fetch(`/api/designs/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (isAuthFailureStatus(res.status)) {
                setAccountEnabled(false);
                notifyAuthExpired();
                setSaveNote("You're signed out — sign in to open designs from your account");
                return false;
            }
            const json = await res.json().catch(() => null);
            if (!res.ok || !json?.success || !json.design?.data) {
                setSaveNote(json?.error || 'Could not load that design');
                return false;
            }
            applySnap(json.design.data);
            setLinkedAccountId(id);
            try {
                const at = Date.now();
                localStorage.setItem(
                    optsRef.current.storageKey,
                    JSON.stringify({ at, name: json.design.name, state: json.design.data })
                );
                setLocalSaved({ at, name: json.design.name });
            } catch { /* the design is in the account regardless */ }
            setSavedAt(Date.now());
            setSaveNote(`Loaded "${json.design.name}" from your account`);
            return true;
        } catch {
            setSaveNote('Could not load that design');
            return false;
        } finally {
            setAccountBusy(false);
        }
    }, [applySnap, setLinkedAccountId]);

    const loadLocalSaved = useCallback((): boolean => {
        let parsed: any = null;
        try {
            const raw = localStorage.getItem(optsRef.current.storageKey);
            if (raw) parsed = JSON.parse(raw);
        } catch { parsed = null; }
        if (!parsed || !parsed.state) {
            setSaveNote('No saved design in this browser yet');
            return false;
        }
        applySnap(parsed.state);
        if (parsed.at) setSavedAt(parsed.at);
        setSaveNote('Loaded your saved design');
        return true;
    }, [applySnap]);

    const deleteAccountDesign = useCallback(async (id: string): Promise<void> => {
        const token = authToken();
        if (!token) return;
        setAccountBusy(true);
        try {
            const res = await fetch(`/api/designs/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            if (isAuthFailureStatus(res.status)) {
                setAccountEnabled(false);
                notifyAuthExpired();
                setSaveNote("You're signed out — sign in to manage account designs");
                return;
            }
            if (id === accountId) setLinkedAccountId(null);
            setSaveNote('Design deleted from your account');
            refreshAccountDesigns();
        } catch {
            setSaveNote('Could not delete that design');
        } finally {
            setAccountBusy(false);
        }
    }, [accountId, refreshAccountDesigns, setLinkedAccountId]);

    // Keyboard: Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z / Ctrl+Y redo.
    // Skipped while typing so text fields keep their native undo.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
            if (!(e.ctrlKey || e.metaKey)) return;
            const key = e.key.toLowerCase();
            if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
            else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [undo, redo]);

    return {
        undo, redo, save, canUndo, canRedo, dirty, savedAt, saveNote,
        accountEnabled, accountDesigns, accountId, accountBusy,
        saveToAccount, loadAccountDesign, deleteAccountDesign, refreshAccountDesigns,
        localSaved, loadLocalSaved,
        restore: applySnap,
        clearWork, persistWorkNow,
        pendingPrevious, acceptPrevious, dismissPrevious, clearPrevious, pinPreviousNow
    };
}
